/**
 * NADT brand mark — a hand-drawn depot-and-tower silhouette inside an oval
 * historical-society seal. Not from an icon library. Original artwork.
 *
 *   • Oval seal frame with a thin inner rule (museum / archive feel)
 *   • A country-station silhouette on the left (hip-roof, chimney, platform)
 *   • A signal tower on the right with a lit lamp
 *   • Two horizontal parallel rails under both (the classic depotmaps trope)
 *   • "EST 2026" cartouche at the bottom
 *
 * Colors come from CSS: `currentColor` for the frame + strokes; `--brand-lamp`
 * for the amber lantern (falls back to Tailwind amber-400).
 */
export function DepotSeal({ className = "", size = 44, showEst = true }) {
  return (
    <svg
      viewBox="0 0 120 120"
      width={size}
      height={size}
      className={className}
      aria-label="North American Depots & Towers"
      role="img"
    >
      {/* Outer seal frame */}
      <ellipse
        cx="60"
        cy="60"
        rx="54"
        ry="54"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
      />
      {/* Inner hairline rule */}
      <ellipse
        cx="60"
        cy="60"
        rx="48"
        ry="48"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.6"
        opacity="0.6"
      />

      {/* Ground line */}
      <path d="M 20 82 L 100 82" stroke="currentColor" strokeWidth="1.4" />
      {/* Two rails */}
      <path d="M 22 86 L 98 86" stroke="currentColor" strokeWidth="0.9" opacity="0.75" />
      <path d="M 24 90 L 96 90" stroke="currentColor" strokeWidth="0.7" opacity="0.55" />
      {/* Tie marks */}
      {[30, 44, 58, 72, 86].map((x) => (
        <rect key={x} x={x - 2} y="87" width="4" height="4" fill="currentColor" opacity="0.35" />
      ))}

      {/* Depot building — hip roof + body + door + windows + chimney */}
      <g stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" fill="none">
        {/* body */}
        <path d="M 26 82 L 26 62 L 58 62 L 58 82 Z" />
        {/* hipped roof w/ eaves */}
        <path d="M 22 62 L 30 52 L 54 52 L 62 62 Z" />
        {/* platform overhang */}
        <path d="M 20 82 L 64 82" strokeWidth="1.1" />
        {/* door */}
        <path d="M 39 82 L 39 70 L 45 70 L 45 82" />
        {/* windows */}
        <rect x="30" y="68" width="6" height="6" />
        <rect x="48" y="68" width="6" height="6" />
        {/* chimney */}
        <path d="M 34 52 L 34 46 L 38 46 L 38 52" />
      </g>

      {/* Interlocking tower on the right — 2-story wooden tower with a
          wide bay-windowed upper storey, hipped roof, and small chimney.
          Modeled on classic 19th-c. railroad interlocking towers (e.g. HB Tower,
          Tower 55) — a working operator's cabin, not a radio antenna. */}
      <g stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" fill="none">
        {/* Ground-floor base (brick/stone) */}
        <path d="M 76 82 L 76 68 L 100 68 L 100 82 Z" />
        {/* Belt course between floors */}
        <path d="M 74 68 L 102 68" strokeWidth="1.2" />
        {/* Upper storey — wider than the base (overhang, classic bay-window profile) */}
        <path d="M 74 68 L 74 54 L 102 54 L 102 68" />
        {/* Hipped roof with eaves */}
        <path d="M 72 54 L 78 46 L 98 46 L 104 54" />
        <path d="M 78 46 L 98 46" strokeWidth="1.1" />
        {/* Chimney */}
        <path d="M 94 46 L 94 40 L 98 40 L 98 46" />
        {/* Ground-floor door + small window */}
        <path d="M 82 82 L 82 74 L 87 74 L 87 82" />
        <rect x="91" y="72" width="6" height="6" />
        {/* Upper-storey bay windows (three panes across the front) */}
        <rect x="76.5" y="58" width="6" height="7" />
        <rect x="85" y="58" width="6" height="7" />
        <rect x="93.5" y="58" width="6" height="7" />
        {/* Vertical muntins in center window */}
        <path d="M 88 58 L 88 65" strokeWidth="0.6" opacity="0.6" />
      </g>
      {/* Lit lamp/signal mounted on the roof peak */}
      <circle cx="88" cy="42" r="1.6" fill="var(--brand-lamp, #fbbf24)" />
      <circle cx="88" cy="42" r="2.6" fill="none" stroke="var(--brand-lamp, #fbbf24)" strokeWidth="0.5" opacity="0.55" />

      {/* Compass-rose star as a maker's mark, top-middle */}
      <g transform="translate(60 22)" fill="currentColor" opacity="0.85">
        <path d="M 0 -6 L 1.3 -1.3 L 6 0 L 1.3 1.3 L 0 6 L -1.3 1.3 L -6 0 L -1.3 -1.3 Z" />
      </g>

      {/* Optional bottom cartouche */}
      {showEst && (
        <>
          <path
            d="M 34 100 Q 60 108 86 100"
            stroke="currentColor"
            strokeWidth="0.7"
            fill="none"
            opacity="0.75"
          />
          <text
            x="60"
            y="103"
            textAnchor="middle"
            fontFamily="ui-serif, Georgia, 'Times New Roman', serif"
            fontSize="6.4"
            letterSpacing="1.8"
            fill="currentColor"
            opacity="0.85"
          >
            EST · MMXXVI
          </text>
        </>
      )}
    </svg>
  );
}
